import { ChangeDetectionStrategy, ChangeDetectorRef, Component, DestroyRef, inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { NotificationsRepoService } from '@client/app/feature-modules/navigation/notifications/services/notifications-repo.service';
import { NotificationType } from '@client/app/feature-modules/navigation/notifications/notifications.types';
import { NotificationsRedirectService } from '@client/app/feature-modules/navigation/notifications/services/notifications-redirect.service';
import { TableWrapperComponent, TableWrapperHeader } from '@client/app/shared-modules/table/components/table-wrapper.component';
import { BreadcrumbService } from '@client/app/feature-modules/shared/components/breadcrumb/services/breadcrumb.service';
import { BreadcrumbComponent } from '@client/app/feature-modules/shared/components/breadcrumb/breadcrumb.component';
import { PageLayoutComponent } from '@client/app/feature-modules/shared/components/page-layout/page-layout.component';
import { TableCellTemplateDirective } from '@client/app/shared-modules/table/directives/table-cell-template.directive';
import { NotificationActionPipe } from '@client/app/feature-modules/navigation/notifications/pipes/notification-action.pipe';
import { NotificationNamePipe } from '@client/app/feature-modules/navigation/notifications/pipes/notification-name.pipe';
import { DEFAULT_PAGE_SIZE } from '@client/app/shared-modules/api/constants/page-size.constant';
import { ParamService } from '@monsido/core/param/param.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { StateService } from '@uirouter/angular';
import { MenuItem } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { TablePageEvent } from 'primeng/table';

@Component({
    selector: 'mon-notifications-page',
    templateUrl: './notifications-page.component.html',
    styleUrls: ['./notifications-page.component.css'],
    standalone: true,
    imports: [
        CommonModule,
        TranslocoModule,
        BreadcrumbComponent,
        PageLayoutComponent,
        TableWrapperComponent,
        TableCellTemplateDirective,
        NotificationActionPipe,
        NotificationNamePipe,
        ButtonModule,
    ],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class NotificationsPageComponent implements OnInit {
    notifications: NotificationType[] = [];
    totalCount = 0;
    loading = false;
    page = 1;
    pageSize = DEFAULT_PAGE_SIZE;
    showBreadcrumbs = false;
    breadcrumbItems: MenuItem[] = [];

    readonly TABLE_SCHEMA: Record<string, TableWrapperHeader<NotificationType>>;

    private readonly notificationsRepoService = inject(NotificationsRepoService);
    private readonly notificationsRedirectService = inject(NotificationsRedirectService);
    private readonly cdRef = inject(ChangeDetectorRef);
    private readonly translocoService = inject(TranslocoService);
    private readonly breadcrumbService = inject(BreadcrumbService);
    private readonly paramService = inject(ParamService);
    private readonly destroyRef = inject(DestroyRef);
    private readonly stateService = inject(StateService);

    constructor () {
        this.TABLE_SCHEMA = TableWrapperComponent.createTableSchema<NotificationType>({
            NOTIFICATION: {
                label: this.translocoService.translate('notifications.page.table.notification'),
                id: 'action',
                type: 'custom',
            },
            DATE: {
                label: this.translocoService.translate('notifications.page.table.date'),
                id: 'updated_at',
                type: 'custom',
            },
        });
    }

    ngOnInit (): void {
        const params = this.paramService.getParams();
        if (params.page_size) {
            this.pageSize = Number.parseInt(String(params.page_size), 10);
        }
        if (params.page) {
            this.page = Number.parseInt(String(params.page), 10);
        }

        const deregisterHook = this.paramService.subscribeParamsChangeOnCurrentState(updated => {
            if (updated.page_size) {
                this.pageSize = Number.parseInt(String(updated.page_size), 10);
            }
            if (updated.page) {
                this.page = Number.parseInt(String(updated.page), 10);
            }
            this.loadPage();
        }) as () => void;
        this.destroyRef.onDestroy(() => deregisterHook());

        this.loadPage();
        this.setBreadcrumbs();

        this.breadcrumbService.breadcrumbs$
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(items => {
                const mapped = items.map((item, index) => {
                    const isLast = index === items.length - 1;
                    if (isLast) {
                        return {
                            label: this.translocoService.translate(item.label),
                        };
                    }
                    return {
                        label: this.translocoService.translate(item.label),
                        url: item.state,
                        command: (): void => {
                            if (item.params) {
                                this.stateService.go(item.state, item.params);
                            } else {
                                this.stateService.go(item.state);
                            }
                        },
                    };
                });
                this.breadcrumbItems = mapped;
                this.showBreadcrumbs = items.length > 0;
                this.cdRef.markForCheck();
            });
    }

    loadPage (): void {
        this.loading = true;
        this.cdRef.markForCheck();

        this.notificationsRepoService
            .getAll({ page: this.page, page_size: this.pageSize, sort_by: 'updated_at', sort_dir: 'desc' })
            .then((result) => {
                const collection = result as unknown as NotificationType[] & { total?: number };
                this.notifications = Array.isArray(collection) ? collection : [];
                this.totalCount = collection.total ?? this.notifications.length;
                this.loading = false;
                this.cdRef.markForCheck();
            })
            .catch(() => {
                this.loading = false;
                this.cdRef.markForCheck();
            });
    }

    onPageChange (event: TablePageEvent): void {
        const { first, rows } = event;
        const pageSizeChanged = rows !== this.pageSize;
        const newPage = pageSizeChanged ? 1 : Math.floor((first || 0) / (rows || 1)) + 1;
        this.paramService.updateParams({ page: newPage, page_size: rows });
    }

    redirect (event: Event, notification: NotificationType): void {
        event.preventDefault();
        event.stopPropagation();
        this.notificationsRedirectService.redirect({
            scopes: Array.isArray(notification.scopes) ? notification.scopes : [notification.scopes],
            action: notification.action,
            target: notification.target,
            target_id: notification.target_id,
        });
    }

    setBreadcrumbs (): void {
        this.breadcrumbService.setBreadcrumbs({
            uiStates: [
                {
                    label: this.translocoService.translate('notifications.page.breadcrumb.domains'),
                    state: 'base.customer.dashboard',
                },
                {
                    label: this.translocoService.translate('notifications.page.breadcrumb.notifications'),
                    state: '',
                },
            ],
        });
    }
}
